import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    alpha,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Container,
    Fade,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import { isHomeroomTeacherRole } from "../../../utils/staffRoles";

const config = {
    Subject: {
        title: "Choose a class",
        subtitle: "Curriculum",
        description:
            "Pick a class to view its subjects — compulsory vs elective, MIE syllabus codes, and periods per week. You can add subjects from the class list.",
        backTo: "/Admin/classes",
        backLabel: "Back to classes",
        hero: "secondary",
        Icon: MenuBookOutlinedIcon,
    },
    Teacher: {
        title: "Choose a class",
        subtitle: "Staffing",
        description:
            "Select a class, then assign subjects. Teachers and heads of department can teach multiple subjects — add them on the next screen.",
        backTo: "/Admin/staff/role",
        backLabel: "Back to role",
        hero: "primary",
        Icon: Groups2OutlinedIcon,
    },
    TeacherAssign: {
        title: "Add teaching subjects",
        subtitle: "Staffing",
        description:
            "Pick a class, then assign one or more subjects to this teacher. Use “Add subject” for each class they teach in.",
        backTo: null,
        backLabel: "Back to teacher profile",
        hero: "primary",
        Icon: Groups2OutlinedIcon,
    },
    Student: {
        title: "Choose a class",
        subtitle: "Enrolment",
        description: "Pick the class you are enrolling a student into. You can add more learners from the class roster after saving.",
        backTo: "/Admin/students",
        backLabel: "Back to students",
        hero: "primary",
        Icon: Groups2OutlinedIcon,
    },
    BulkUpload: {
        title: "Choose a class for bulk upload",
        subtitle: "Bulk enrolment",
        description:
            "Pick the class to import students into. Every row in your CSV or Excel file will be enrolled in that class.",
        backTo: "/Admin/students",
        backLabel: "Back to students",
        hero: "primary",
        Icon: UploadFileOutlinedIcon,
    },
};

function ClassCardSkeleton() {
    return (
        <Grid item xs={12} sm={6} md={4}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
        </Grid>
    );
}

const ChooseClass = ({ situation }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const staffRole = location.state?.staffRole;
    const dispatch = useDispatch();
    const { sclassesList, loading, error, getresponse } = useSelector((state) => state.sclass);
    const { currentUser } = useSelector((state) => state.user);

    const assignTeacherId = situation === "TeacherAssign" ? params.teacherID || "" : "";

    const mode =
        situation === "TeacherAssign"
            ? "TeacherAssign"
            : situation === "Teacher"
              ? "Teacher"
              : situation === "Student"
                ? "Student"
                : "Subject";
    const c = config[mode];
    const HeroIcon = c.Icon;
    const backTo =
        mode === "TeacherAssign" && assignTeacherId
            ? `/Admin/teachers/teacher/${assignTeacherId}`
            : c.backTo;

    useEffect(() => {
        if (currentUser?._id) {
            dispatch(getAllSclasses(currentUser._id, "Sclass"));
        }
    }, [currentUser?._id, dispatch]);

    const rows = useMemo(() => {
        if (!Array.isArray(sclassesList) || sclassesList.length === 0) return [];
        return sclassesList.map((sclass) => ({
            id: sclass._id,
            name: sclass.sclassName,
            room: typeof sclass.room === "string" ? sclass.room.trim() : sclass.room || "",
            capacity: sclass.studentCapacity,
        }));
    }, [sclassesList]);

    const navigateHandler = (classID) => {
        const roleState = staffRole ? { state: { staffRole } } : undefined;
        if (situation === "TeacherAssign" && assignTeacherId) {
            navigate(`/Admin/teachers/choosesubject/${classID}/${assignTeacherId}`, roleState);
        } else if (situation === "Teacher") {
            if (isHomeroomTeacherRole(staffRole)) {
                const row = rows.find((r) => r.id === classID);
                navigate(`/Admin/staff/homeroom/${classID}`, {
                    state: {
                        staffRole,
                        className: row?.name || "",
                    },
                });
            } else {
                navigate(`/Admin/teachers/choosesubject/${classID}`, roleState);
            }
        } else if (situation === "Student") {
            navigate("/Admin/class/addstudents/" + classID);
        } else if (situation === "BulkUpload") {
            navigate(`/Admin/class/${classID}/bulk-upload`);
        } else if (situation === "Subject") {
            const row = rows.find((r) => r.id === classID);
            navigate("/Admin/subjects/class/" + classID, {
                state: { className: row?.name || "" },
            });
        }
    };

    const paletteKey = c.hero;
    const hasRows = rows.length > 0;
    const showEmpty = !loading && !error && !hasRows && !getresponse;
    const showCards = !loading && !error && hasRows;

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                pb: 6,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette[paletteKey].main, 0.06)} 0%, ${t.palette.grey[50]} 30%)`
                        : alpha(t.palette.common.black, 0.2),
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 } }}>
                <Fade in timeout={320}>
                    <Stack spacing={3}>
                        <Button
                            startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => navigate(backTo || "/Admin/teachers")}
                            sx={{ alignSelf: "flex-start", fontWeight: 600 }}
                        >
                            {c.backLabel}
                        </Button>

                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette[paletteKey].dark} 0%, ${t.palette[paletteKey].main} 52%, ${alpha(
                                        t.palette[paletteKey].light,
                                        0.9
                                    )} 100%)`,
                                boxShadow: (t) => `0 14px 44px ${alpha(t.palette[paletteKey].main, 0.28)}`,
                            }}
                        >
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: alpha("#fff", 0.15),
                                        flexShrink: 0,
                                    }}
                                >
                                    <HeroIcon sx={{ fontSize: 32 }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.1 }}>
                                        {c.subtitle}
                                    </Typography>
                                    <Typography variant="h4" component="h1" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                                        {c.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 640, mt: 0.75 }}>
                                        {c.description}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<AddCircleOutlineOutlinedIcon />}
                                    onClick={() => navigate("/Admin/addclass")}
                                    sx={{
                                        alignSelf: { xs: "stretch", sm: "center" },
                                        bgcolor: "common.white",
                                        color: `${paletteKey}.main`,
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        boxShadow: "none",
                                        whiteSpace: "nowrap",
                                        "&:hover": {
                                            bgcolor: (t) => alpha(t.palette.common.white, 0.92),
                                            boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.12)}`,
                                        },
                                    }}
                                >
                                    New class
                                </Button>
                            </Stack>
                        </Paper>

                        {error && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {error?.message || "Could not load classes."}
                            </Alert>
                        )}

                        {getresponse && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 3, sm: 4 },
                                    borderRadius: 3,
                                    textAlign: "center",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                }}
                            >
                                <ClassOutlinedIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    No classes yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto", mb: 2 }}>
                                    {typeof getresponse === "string" ? getresponse : "Create a class first, then return here to add subjects."}
                                </Typography>
                                <Button variant="contained" size="large" onClick={() => navigate("/Admin/addclass")}>
                                    Add class
                                </Button>
                            </Paper>
                        )}

                        {loading && (
                            <Grid container spacing={2}>
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <ClassCardSkeleton key={i} />
                                ))}
                            </Grid>
                        )}

                        {showEmpty && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 3, sm: 4 },
                                    borderRadius: 3,
                                    textAlign: "center",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                }}
                            >
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    No classes to show
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Add at least one class to continue.
                                </Typography>
                                <Button variant="contained" onClick={() => navigate("/Admin/addclass")}>
                                    Add class
                                </Button>
                            </Paper>
                        )}

                        {showCards && (
                            <Grid container spacing={2}>
                                {rows.map((row) => (
                                    <Grid item xs={12} sm={6} md={4} key={row.id}>
                                        <Card
                                            elevation={0}
                                            sx={{
                                                height: "100%",
                                                borderRadius: 2.5,
                                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                                transition: (t) =>
                                                    t.transitions.create(["box-shadow", "transform", "border-color"], {
                                                        duration: t.transitions.duration.shorter,
                                                    }),
                                                "&:hover": {
                                                    borderColor: (t) => alpha(t.palette[paletteKey].main, 0.45),
                                                    boxShadow: (t) => `0 10px 32px ${alpha(t.palette[paletteKey].main, 0.18)}`,
                                                    transform: "translateY(-2px)",
                                                },
                                            }}
                                        >
                                            <CardActionArea onClick={() => navigateHandler(row.id)} sx={{ height: "100%", alignItems: "stretch" }}>
                                                <CardContent sx={{ p: 2.5, height: "100%" }}>
                                                    <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                                                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 44,
                                                                    height: 44,
                                                                    borderRadius: 2,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    bgcolor: (t) => alpha(t.palette[paletteKey].main, 0.1),
                                                                    color: `${paletteKey}.main`,
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                <ClassOutlinedIcon />
                                                            </Box>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography variant="h6" fontWeight={800} noWrap title={row.name}>
                                                                    {row.name}
                                                                </Typography>
                                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                                                    {row.room ? (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Room: {row.room}
                                                                        </Typography>
                                                                    ) : null}
                                                                    {row.capacity != null && row.capacity !== "" ? (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Capacity {row.capacity}
                                                                        </Typography>
                                                                    ) : null}
                                                                </Stack>
                                                            </Box>
                                                        </Stack>
                                                        <ChevronRightIcon color="action" sx={{ flexShrink: 0, mt: 0.5 }} />
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
                                                        {situation === "Subject"
                                                            ? "View subjects in this class"
                                                            : situation === "Student"
                                                              ? "Register a new student in this class"
                                                              : situation === "TeacherAssign"
                                                                ? "Assign subjects in this class"
                                                                : isHomeroomTeacherRole(staffRole)
                                                                  ? "Choose subjects, then create their login"
                                                                  : "Pick subjects and register a teacher"}
                                                    </Typography>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Stack>
                </Fade>
            </Container>
        </Box>
    );
};

export default ChooseClass;
